import { supabase } from '../supabase/supabase';

const PROFILE_TABLES = [
  'platform_profiles_1788695942781',
  'platform_profiles'
];

const getError = error => ({
  success: false,
  error: error || { message: 'Ukjent databasefeil.' }
});

const firstSuccessfulQuery = async buildQuery => {
  let lastError = null;

  for (const table of PROFILE_TABLES) {
    const result = await buildQuery(table);

    if (!result.error) {
      return result;
    }

    lastError = result.error;
  }

  return { data: [], error: lastError };
};

const parseErrorBody = body => {
  if (!body) return null;

  if (typeof body === 'object' && body.message) {
    return body.message;
  }

  if (typeof body !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(body);
    return parsed?.message || null;
  } catch {
    return body;
  }
};

const getFunctionErrorDetails = async error => {
  const context = error?.context;
  let body = context?.body || context?.response?.body || context?.response || context || null;

  if (body && typeof body.text === 'function') {
    try {
      body = await body.text();
    } catch {
      body = null;
    }
  }

  const bodyMessage = parseErrorBody(body);
  const status = context?.status ||
    context?.response?.status ||
    (typeof context?.status === 'number' ? context.status : null) ||
    error?.status ||
    null;
  const message = bodyMessage || error?.message || 'Invitasjonsfunksjonen feilet.';

  return {
    status,
    message,
    raw: `${bodyMessage || ''} ${error?.message || ''}`.trim()
  };
};

export const listOrganizations = async () => {
  const result = await supabase
    .from('platform_organizations')
    .select('id, name, slug, website, contact_email, status, plan, created_at, updated_at')
    .order('name', { ascending: true });

  return result.error ? getError(result.error) : { success: true, data: result.data || [] };
};

export const saveOrganization = async organization => {
  const values = {
    name: organization.name?.trim(),
    slug: organization.slug?.trim().toLowerCase(),
    website: organization.website?.trim() || '',
    contact_email: organization.contact_email?.trim().toLowerCase() || '',
    status: organization.status || 'active',
    plan: organization.plan || 'internal'
  };

  if (!values.name || !values.slug) {
    return getError({ message: 'Navn og slug må fylles ut.' });
  }

  const query = organization.id
    ? supabase
      .from('platform_organizations')
      .update(values)
      .eq('id', organization.id)
      .select()
      .single()
    : supabase
      .from('platform_organizations')
      .insert(values)
      .select()
      .single();

  const { data, error } = await query;
  return error ? getError(error) : { success: true, data };
};

export const findProfileByEmail = async email => {
  const normalizedEmail = email?.trim().toLowerCase();

  if (!normalizedEmail) {
    return getError({ message: 'E-post må fylles ut.' });
  }

  const result = await firstSuccessfulQuery(table =>
    supabase
      .from(table)
      .select('id, email, name, role, status')
      .ilike('email', normalizedEmail)
      .limit(1)
      .maybeSingle()
  );

  return result.error
    ? getError(result.error)
    : { success: true, data: result.data || null };
};

const listTenantInvites = async tenantId => {
  const result = await supabase
    .from('platform_employee_invites_1788697000000')
    .select('*')
    .eq('tenant_id', tenantId);

  return result;
};

export const listTenantMemberships = async tenantId => {
  const membershipsResult = await supabase
    .from('platform_tenant_memberships')
    .select('id, tenant_id, user_id, role, status, created_at, updated_at')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: true });

  if (membershipsResult.error) return getError(membershipsResult.error);

  const memberships = membershipsResult.data || [];
  const userIds = memberships.map(item => item.user_id);

  if (userIds.length === 0) {
    return { success: true, data: [] };
  }

  const profilesResult = await firstSuccessfulQuery(table =>
    supabase
      .from(table)
      .select('id, email, name, role, status')
      .in('id', userIds)
  );

  if (profilesResult.error) return getError(profilesResult.error);

  const profilesById = new Map(
    (profilesResult.data || []).map(profile => [profile.id, profile])
  );

  const invitesResult = await listTenantInvites(tenantId);
  const invitesByUserId = new Map(
    (invitesResult.data || [])
      .filter(invite => invite.user_id)
      .map(invite => [invite.user_id, invite])
  );

  return {
    success: true,
    data: memberships.map(membership => ({
      ...membership,
      profile: profilesById.get(membership.user_id) || null,
      invite: profilesById.get(membership.user_id)
        ? null
        : invitesByUserId.get(membership.user_id) || null
    }))
  };
};

export const saveMembership = async ({ tenantId, email, role, status = 'active' }) => {
  const profileResult = await findProfileByEmail(email);

  if (!profileResult.success) return profileResult;
  if (!profileResult.data) {
    return getError({
      code: 'PROFILE_NOT_FOUND',
      message: 'Brukeren må først opprettes i Supabase Auth eller registrere seg med samme e-post.'
    });
  }

  const { data, error } = await supabase
    .from('platform_tenant_memberships')
    .upsert({
      tenant_id: tenantId,
      user_id: profileResult.data.id,
      role,
      status
    }, {
      onConflict: 'tenant_id,user_id'
    })
    .select()
    .single();

  return error
    ? getError(error)
    : { success: true, data, profile: profileResult.data };
};

const findInviteByEmail = async email => {
  const normalizedEmail = email?.trim().toLowerCase();

  return supabase
    .from('platform_employee_invites_1788697000000')
    .select('id')
    .ilike('email', normalizedEmail)
    .limit(1)
    .maybeSingle();
};

export const registerInvite = async ({ tenantId, name, email, role, createdBy }) => {
  if (![
    'Admin',
    'SalesManager',
    'Sales',
    'Researcher',
    'Support',
    'Finance',
    'Viewer'
  ].includes(role)) {
    return getError({ message: 'Ugyldig rolle for kundeorganisasjon.' });
  }

  const inviteRole = role === 'Admin' ? 'Admin' : role;
  const inviteValues = {
    tenant_id: tenantId,
    name: name?.trim() || '',
    email: email?.trim().toLowerCase(),
    role: inviteRole,
    status: 'Pending',
    created_by: createdBy
  };

  const existingInviteResult = await findInviteByEmail(inviteValues.email);

  if (existingInviteResult.error) {
    return getError(existingInviteResult.error);
  }

  if (existingInviteResult.data) {
    const { data, error } = await supabase
      .from('platform_employee_invites_1788697000000')
      .update(inviteValues)
      .eq('id', existingInviteResult.data.id)
      .select()
      .single();

    if (error) return getError(error);

    return {
      success: true,
      data,
      warning: 'Invitasjon er oppdatert.'
    };
  }

  const { data, error } = await supabase
    .from('platform_employee_invites_1788697000000')
    .insert(inviteValues)
    .select()
    .single();

  if (error) return getError(error);

  return {
    success: true,
    data,
    warning: 'Invitasjon er registrert, men brukeren må opprette konto med samme e-post eller opprettes i Supabase Auth før tilgang virker.'
  };
};

export const inviteTenantUser = async ({ tenant_id, email, name, role }) => {
  const { data, error } = await supabase.functions.invoke('invite-tenant-user', {
    body: {
      tenant_id,
      email,
      name: name || '',
      role
    }
  });

  if (error) {
    const details = await getFunctionErrorDetails(error);

    console.error('[TenantAdminService] invite-tenant-user failed', {
      error,
      details
    });

    const isFunctionMissing = details.status === 404 || /function not found|function does not exist|not found/i.test(
      details.raw
    );

    return getError({
      code: isFunctionMissing
        ? 'INVITE_FUNCTION_UNAVAILABLE'
        : 'INVITE_FUNCTION_FAILED',
      message: isFunctionMissing
        ? 'Invitasjonsfunksjon er ikke deployet ennå. Opprett bruker manuelt i Supabase Auth og koble deretter medlemskap.'
        : details.message
    });
  }

  if (!data?.ok) {
    console.error('[TenantAdminService] invite-tenant-user returned an error', {
      code: data?.code,
      message: data?.message
    });

    return getError({
      code: data?.code || 'INVITE_FUNCTION_FAILED',
      message: data?.message || 'Invitasjonen kunne ikke fullføres.'
    });
  }

  return {
    success: true,
    data,
    message: data.message
  };
};

export const updateMembership = async (membershipId, changes) => {
  const { data, error } = await supabase
    .from('platform_tenant_memberships')
    .update(changes)
    .eq('id', membershipId)
    .select()
    .single();

  return error ? getError(error) : { success: true, data };
};